import React, { useRef, useState } from 'react';
import styles from './VideoPlayer.module.css';

const formatTime = (s) => {
  if (!isFinite(s) || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8 5v14l11-7z"/></svg>
);
const IconPause = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
);
const IconVolume = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
);
const IconVolumeLow = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/></svg>
);
const IconMute = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
);
const IconFullscreen = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
);

const VideoPlayer = ({ src, poster, wrapperStyle, videoStyle }) => {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); }
    else { videoRef.current.play(); setIsPlaying(true); }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = (v.currentTime / v.duration) * 100;
    setProgress(isFinite(p) ? p : 0);
    setCurrentTime(v.currentTime);
    setDuration(v.duration || 0);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(Math.max(((e.clientX - rect.left) / rect.width) * 100, 0), 100);
    if (videoRef.current) {
      videoRef.current.currentTime = (pct / 100) * videoRef.current.duration;
      setProgress(pct);
    }
  };

  const handleVolume = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const vol = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    if (videoRef.current) videoRef.current.volume = vol;
    setVolume(vol);
    setIsMuted(vol === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
    if (!next) { setVolume(1); videoRef.current.volume = 1; }
    else setVolume(0);
  };



  const toggleFullscreen = () => {
    if (!wrapperRef.current) return;
    const elem = wrapperRef.current;
    
    if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    } else {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
      }
    }
  };

  const VolumeIcon = isMuted || volume === 0 ? IconMute : volume > 0.5 ? IconVolume : IconVolumeLow;

  return (
    <div className={styles.wrapper} style={wrapperStyle} ref={wrapperRef}>
      <video
        ref={videoRef}
        className={styles.video}
        style={videoStyle}
        src={`${src}#t=0.001`}
        preload="metadata"
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        onClick={togglePlay}
      />

      {!isPlaying && (
        <button className={styles.bigPlay} onClick={togglePlay} aria-label="Reproducir">
          <IconPlay />
        </button>
      )}

      <div className={styles.controls}>
        <div className={styles.progressRow}>
          <span className={styles.time}>{formatTime(currentTime)}</span>
          <div className={styles.track} onClick={handleSeek}>
            <div className={styles.fill} style={{ width: `${progress}%` }} />
          </div>
          <span className={styles.time}>{formatTime(duration)}</span>
        </div>

        <div className={styles.ctrlRow}>
          <div className={styles.left}>
            <button className={styles.btn} onClick={togglePlay} aria-label="Play/Pause">
              {isPlaying ? <IconPause /> : <IconPlay />}
            </button>
            <button className={styles.btn} onClick={toggleMute} aria-label="Silenciar">
              <VolumeIcon />
            </button>
            <div className={styles.volTrack} onClick={handleVolume}>
              <div className={styles.fill} style={{ width: `${isMuted ? 0 : volume * 100}%` }} />
            </div>
          </div>

          <div className={styles.right}>
            <button className={styles.btn} onClick={toggleFullscreen} aria-label="Pantalla completa">
              <IconFullscreen />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
